import { inject, Injectable } from "@angular/core";
import { Property, PropertyCreation, PropertyType } from "@rvi/asset-modules";
import { concatMap, forkJoin, map } from "rxjs";
import { TenantConfigSchema } from "../../../_offline/models/tenantConfiguration.schema";
import { TenantConfigurationDbService } from "../../database/tenant-configuration-db.service";
import { AssetModuleClient } from "../asset-module-client.service";
import { Log } from "./log.decorator";

export enum ConfigProperty {
  SerialNumber = "SerialNumber",
  Owner = "Owner",
  Project = "Project",
  Site = "Site",
  Country = "Country",
  State = "State"
}

const TenantConfigs: { dbKey: string; defaultLabel: string; configKey: ConfigProperty }[] = [
  { dbKey: "LabelOfOwner", defaultLabel: "Owner", configKey: ConfigProperty.Owner },
  { dbKey: "LabelOfState", defaultLabel: "State", configKey: ConfigProperty.State },
  { dbKey: "LabelOfCountry", defaultLabel: "Country", configKey: ConfigProperty.Country },
  { dbKey: "LabelOfSite", defaultLabel: "Site", configKey: ConfigProperty.Site },
  { dbKey: "LabelOfProject", defaultLabel: "Project", configKey: ConfigProperty.Project }
];
export type CreatedPropertyDictionnary = Record<ConfigProperty, { propertyId: string }>;

@Injectable({
  providedIn: "root"
})
export class PropertiesMigrationService {
  private tenantConfigDbService = inject(TenantConfigurationDbService);
  private assetModuleClient: AssetModuleClient = inject(AssetModuleClient);

  @Log("migrateSystemProperties")
  public migrateSystemProperties() {
    return forkJoin({ configs: this.tenantConfigDbService.getAllOnPremise(), properties: this.assetModuleClient.getProperties() }).pipe(
      concatMap(({ configs, properties }) => this.createProperties(configs, properties))
    );
  }

  private createProperties(configs: TenantConfigSchema[], properties: Property[]) {
    const systemProperties = TenantConfigs.map((tenantConfig) => {
      const config = configs.find((c) => c.key === tenantConfig.dbKey);
      return {
        name: config?.value ?? tenantConfig.defaultLabel,
        type: PropertyType.Text,
        config: tenantConfig.configKey
      };
    });

    const payload = this.filterExistingProperties(systemProperties, properties);

    return this.assetModuleClient.createProperties(payload).pipe(map((created) => this.toDictionnary(systemProperties, [...properties, ...created])));
  }

  private toDictionnary(
    systemProperties: { name: string; type: PropertyType; config: ConfigProperty }[],
    allProperties: Property[]
  ): CreatedPropertyDictionnary {
    const dict = Object.fromEntries(
      systemProperties.map((p) => {
        const match = allProperties.find((e) => e.name.toLowerCase() === p.name.toLowerCase());

        return [p.config, { propertyId: match?.id ?? "" }];
      })
    ) as CreatedPropertyDictionnary;
    return { ...dict, [ConfigProperty.SerialNumber]: { propertyId: allProperties.find((p) => p.name == "SerialNumber")?.id } };
  }

  private filterExistingProperties(systemProperties: PropertyCreation[], properties: Property[]) {
    return systemProperties.filter((property) => !properties.find((el) => el.name.toLowerCase() == property.name.toLowerCase()));
  }
}
